import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import ServiceListStore from '../../common/stores/ServiceList';
import CategoryListStore from '../../common/stores/CategoryList';
import ConfigStore from '../../common/stores/Config';
import FormattersStore from '../../common/stores/Formatters';
import { fetchServices, categorizeServices } from '../../common/actions/ServiceList';
import { fetchCategories } from '../../common/actions/CategoryList';
import { Fetching } from '../../common/components/Fetching';
import CLSHandler from '../../common/components/CLSHandler';
import Category from './Category';
import dataAids from '../../common/constants/dataAids';
import renderModes from '../../common/constants/renderModes';
import { Translate as translate } from '@wsb/vnext-widget-content-translate';
import { isSingleEvent } from '../../common/utils/recurrenceType';
import { costTypes } from '../../common/constants/costTypes';
import { trackMetric, trackC1 } from '../../common/utils/trackMetric';
import {
  LAYOUT_2_IMPRESSION,
  SERVICES_DISPLAYED_IMPRESSION,
  C1_SERVICES_DISPLAYED_IMPRESSION,
  C1_SERVICES_NOT_DISPLAYED_IMPRESSION
} from '../../common/constants/metrics';

const styles = {
  timeZone: {
    display: 'block',
    marginTop: 'large',
    textAlign: 'center',
    fontColor: 'lowContrast'
  },
  error: {
    width: '100%',
    marginBottom: 'medium',
    marginTop: 'xxlarge',
    textAlign: 'center',
    font: 'alt',
    fontColor: 'error'
  }
};

export default class ServiceList extends Component {
  constructor(props) {
    super(props);
    this.onStoreChange = this.onStoreChange.bind(this);
    this.state = this.getStoreState();
  }

  getStoreState() {
    const configState = ConfigStore.getState();
    const serviceListState = ServiceListStore.getState();
    const categoryListState = CategoryListStore.getState();

    return {
      ...serviceListState,
      error: configState.error || serviceListState.error || categoryListState.error,
      isFetching:
        configState.isFetching || serviceListState.isFetching || categoryListState.isFetching,
      categories: categoryListState.categories,
      timeZone: configState.timeZone,
      maxServicesPerCategory: configState.appointments2_max_services_per_category,
      maxUncategorizedServices: configState.appointments2_max_uncategorized_services,
      maxServiceDescriptionEllipseCharacters:
        configState.appointments2_max_service_description_ellipse_characters,
      websiteId: configState.websiteId
    };
  }

  componentDidMount() {
    ConfigStore.addListener('change', this.onStoreChange);
    ServiceListStore.addListener('change', this.onStoreChange);
    CategoryListStore.addListener('change', this.onStoreChange);
    const { areServicesHidden } = this.props;

    trackMetric(LAYOUT_2_IMPRESSION);

    if (areServicesHidden) {
      trackC1(C1_SERVICES_NOT_DISPLAYED_IMPRESSION);
      return;
    }

    setTimeout(() => {
      fetchServices()
        .then(isSuccess => isSuccess && fetchCategories())
        .then(isSuccess => {
          if (!isSuccess) return;
          const { services, categories } = this.state;
          if (!services.length) {
            trackC1(C1_SERVICES_NOT_DISPLAYED_IMPRESSION);
          } else {
            trackC1(C1_SERVICES_DISPLAYED_IMPRESSION);
          }
          categorizeServices(services, categories);
        });
    }, 0);
  }

  componentWillUnmount() {
    ConfigStore.removeListener('change', this.onStoreChange);
    ServiceListStore.removeListener('change', this.onStoreChange);
    CategoryListStore.removeListener('change', this.onStoreChange);
  }

  componentDidUpdate() {
    if (!this.hasLoggedListImpression && this.state.services.length) {
      trackMetric(SERVICES_DISPLAYED_IMPRESSION);
      this.hasLoggedListImpression = true;
    }
  }

  onStoreChange() {
    this.setState(this.getStoreState());
  }

  getMaxServicesShown() {
    const { categorizedServices, maxUncategorizedServices, maxServicesPerCategory } = this.state;

    return categorizedServices.length === 1 && categorizedServices[0].id === null
      ? maxUncategorizedServices
      : maxServicesPerCategory;
  }

  isErrorShown() {
    const { error } = this.state;
    const { renderMode, isGetStartedCtaDisplayed } = this.props;

    if (!error) return false;
    if (isGetStartedCtaDisplayed) return false;
    if ([renderModes.ADD, renderModes.DISPLAY, renderModes.SAMPLE].includes(renderMode))
      return false;
    return true;
  }

  isServicesSectionShown = ({ areServicesShown, areServicesComingShown }) => {
    return areServicesShown || areServicesComingShown;
  };

  /* eslint-disable complexity */
  render() {
    const {
      isSampleDataUsed,
      staticContent,
      callToActionText,
      imageCropMethod,
      widgetCategory,
      isGetStartedCtaDisplayed,
      areServicesHidden,
      navigate,
      renderMode,
      section
    } = this.props;

    const {
      isFetching,
      areServicesLoaded,
      areServicesCategorized,
      services,
      maxServiceDescriptionEllipseCharacters
    } = this.state;

    let { categorizedServices } = this.state;

    const {
      Element: { Text },
      Component: {
        Grid,
        Grid: { Cell },
        PlaceholderMessage
      }
    } = UX2;

    const interpolate = translate(staticContent);
    const { dateTimeFormatter } = FormattersStore.getState();

    const areServicesComingShown =
      areServicesHidden || (areServicesLoaded && areServicesCategorized);

    const areServicesShown =
      (!areServicesHidden && categorizedServices.length && services.length) || isSampleDataUsed;

    if (isSampleDataUsed) {
      categorizedServices = [
        {
          id: 0,
          name: null,
          services: [
            {
              image_url: '',
              name: interpolate('editorSampleServices.first.name'),
              cost_type: costTypes.FREE,
              cost: '0',
              description: interpolate('editorSampleServices.first.description'),
              duration: 'PT15M',
              id: 1
            },
            {
              image_url: '',
              name: interpolate('editorSampleServices.second.name'),
              cost_type: costTypes.COST,
              cost: '10.0',
              description: interpolate('editorSampleServices.second.description'),
              duration: 'PT1H',
              id: 2
            },
            {
              image_url: '',
              name: interpolate('editorSampleServices.third.name'),
              cost_type: costTypes.CUSTOM_TEXT,
              custom_cost_text: interpolate('editorSampleServices.third.customCostText'),
              description: interpolate('editorSampleServices.third.description'),
              duration: 'PT30M',
              id: 3
            }
          ]
        }
      ];
    }

    return (
      <Grid xs={ 1 } sm={ 6 }>
        <Cell xs={ 1 } sm={ areServicesShown ? 4 : 6 } pushSm={ areServicesShown ? 1 : 0 }>
          { this.isServicesSectionShown({ areServicesShown, areServicesComingShown }) && (
            <CLSHandler>
              { areServicesShown
                ? categorizedServices.map((category, index) => (
                  <Category
                    key={ category.id || 'uncategorized' }
                    category={ category }
                    maxServicesShown={ this.getMaxServicesShown() }
                    maxServiceDescriptionEllipseCharacters={
                      maxServiceDescriptionEllipseCharacters
                    }
                    hasSeparator={
                      index > 0 &&
                        !(index === categorizedServices.length - 1 && !category.services.length)
                    }
                    staticContent={ staticContent }
                    callToActionText={ callToActionText }
                    imageCropMethod={ imageCropMethod }
                    widgetCategory={ widgetCategory }
                    navigate={ navigate }
                    renderMode={ renderMode }
                  />
                ))
                : areServicesComingShown && (
                  <PlaceholderMessage
                    section={ section === 'default' ? 'alt' : 'default' }
                    message={ staticContent.servicesCommingSoon }
                    textProps={{
                      'data-aid': dataAids.SERVICE_LIST_NO_SERVICES,
                      style: { fontSize: 'large' }
                    }}
                  />
                ) }
            </CLSHandler>
          ) }
          { this.isErrorShown() && (
            <Text style={ styles.error } data-aid={ dataAids.SERVICE_LIST_ERROR }>
              { staticContent.defaultError }
            </Text>
          ) }
          { services.some(service => isSingleEvent(service.recurrence_type)) &&
            !isGetStartedCtaDisplayed && (
            <Text data-aid={ dataAids.SERVICE_LIST_TIME_ZONE } style={ styles.timeZone }>
              { dateTimeFormatter.getTimeZoneDisplayName() }
            </Text>
          ) }
          <Fetching fetching={ isFetching } />
        </Cell>
      </Grid>
    );
  }
  /* eslint-enable complexity */
}

ServiceList.propTypes = {
  renderMode: PropTypes.string,
  navigate: PropTypes.func,
  isSampleDataUsed: PropTypes.bool,
  isGetStartedCtaDisplayed: PropTypes.bool,
  staticContent: PropTypes.object.isRequired,
  callToActionText: PropTypes.string,
  imageCropMethod: PropTypes.string,
  widgetCategory: PropTypes.string,
  areServicesHidden: PropTypes.bool.isRequired,
  olaFbPixelId: PropTypes.string,
  section: PropTypes.string
};
